/**
 * MemberManagement.tsx
 * 
 * 組織メンバー管理コンポーネント
 * Issue #268 - マルチテナント組織管理機能の実装
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridActionsCellItem,
  GridSortModel,
  GridPaginationModel,
  GridColumnVisibilityModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Bundle Size Optimization: Use optimized imports
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Paper,
  Snackbar,
} from '@mui/material';

import type { SelectChangeEvent } from '@mui/material/Select';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';



/**
 * メンバー情報の型定義
 */
export interface MemberInfo {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  tenantRole: 'staff' | 'owner' | 'manager' | 'admin' | 'viewer' | 'member';
  status: 'active' | 'inactive' | 'pending' | 'invited';
  lastActive?: Date;
  permissions: Record<string, unknown>;
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
  invitationToken?: string;
  invitedAt?: Date;
  invitedBy?: string;
}

/**
 * メンバー招待データの型定義
 */
interface MemberInvitationData {
  email: string;
  role: string;
  tenantRole: 'staff' | 'owner' | 'manager' | 'admin' | 'viewer' | 'member';
  message?: string;
  permissions?: Record<string, unknown>;
}

/**
 * 役割オプション定数
 */
const ROLE_OPTIONS = [
  { value: 'owner', label: 'オーナー' },
  { value: 'manager', label: 'マネージャー' },
  { value: 'admin', label: '管理者' },
  { value: 'staff', label: 'スタッフ' },
  { value: 'viewer', label: '閲覧者' },
  { value: 'member', label: 'メンバー' },
];

/**
 * ステータスオプション定数
 */
const STATUS_OPTIONS = [
  { value: 'active', label: 'アクティブ', color: 'success' },
  { value: 'inactive', label: '無効', color: 'error' },
  { value: 'pending', label: '招待中', color: 'warning' },
  { value: 'invited', label: '招待済み', color: 'info' },
];

/**
 * MemberManagementコンポーネントのProps
 */
interface MemberManagementProps {
  organizationId: string;
  currentUserRole: 'owner' | 'admin' | 'manager' | 'staff' | 'viewer';
  onMemberUpdated?: (member: MemberInfo) => void;
  onMemberRemoved?: (memberId: string) => void;
}

/**
 * MemberManagementコンポーネント
 */
export const MemberManagement: React.FC<MemberManagementProps> = ({
  organizationId,
  currentUserRole,
  onMemberUpdated,
  onMemberRemoved,
}) => {
  // Prevent unused variable warnings - these will be implemented in future iterations
  console.debug('MemberManagement props:', { organizationId, currentUserRole, onMemberUpdated: !!onMemberUpdated, onMemberRemoved: !!onMemberRemoved });
  // State management
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  
  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  
  // Form states
  const [invitationData, setInvitationData] = useState<MemberInvitationData>({
    email: '',
    role: 'member',
    tenantRole: 'member',
    message: '',
  });
  
  // DataGrid states
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  
  // Menu states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  /**
   * メンバー一覧の取得
   */
  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: 実際のAPI呼び出しに置き換え
      // organizationIdを使用してAPIリクエストを送信
      const mockMembers: MemberInfo[] = [
        {
          id: '1',
          email: 'owner@example.com',
          firstName: '太郎',
          lastName: '田中',
          role: 'owner',
          tenantRole: 'owner',
          status: 'active',
          lastActive: new Date(),
          permissions: {},
          organizationId: 'org-1',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ];
      
      setMembers(mockMembers);
    } catch (err) {
      setError('メンバー一覧の取得に失敗しました');
      console.error('Failed to fetch members:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * メンバー招待の送信
   */
  const handleSendInvitation = useCallback(async () => {
    try {
      setLoading(true);
      
      // TODO: 実際のAPI呼び出しに置き換え
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSnackbarMessage('招待を送信しました');
      setSnackbarOpen(true);
      setInviteDialogOpen(false);
      setInvitationData({
        email: '',
        role: 'member',
        tenantRole: 'member',
        message: '',
      });
      
      await fetchMembers();
    } catch (err) {
      setError('招待の送信に失敗しました');
      console.error('Failed to send invitation:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchMembers]);



  /**
   * DataGrid列定義
   */
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: '名前',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {`${params.row.lastName || ''} ${params.row.firstName || ''}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tenantRole',
      headerName: '役割',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const roleOption = ROLE_OPTIONS.find(option => option.value === params.value);
        return (
          <Chip
            label={roleOption?.label || params.value}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'ステータス',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const statusOption = STATUS_OPTIONS.find(option => option.value === params.value);
        return (
          <Chip
            label={statusOption?.label || params.value}
            size="small"
            color={statusOption?.color as any || 'default'}
            variant="filled"
          />
        );
      },
    },
    {
      field: 'lastActive',
      headerName: '最終アクティブ',
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return '-';
        return (
          <Typography variant="body2">
            {format(new Date(params.value), 'yyyy/MM/dd HH:mm', { locale: ja })}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'アクション',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="編集"
          onClick={() => {
            console.log('Edit member:', params.row);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="削除"
          onClick={() => {
            console.log('Delete member:', params.row);
          }}
        />,
      ],
    },
  ], []);

  /**
   * 初期化
   */
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">メンバー管理</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchMembers}
            disabled={loading}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            メンバー招待
          </Button>
        </Box>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* データグリッド */}
      <Paper sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={members}
          columns={columns}
          loading={loading}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedMembers as any}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedMembers(Array.isArray(newSelection) ? newSelection as string[] : []);
          }}
          sx={{
            border: 0,
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {/* 招待ダイアログ */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>メンバー招待</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="メールアドレス"
              type="email"
              value={invitationData.email}
              onChange={(e) => setInvitationData(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>役割</InputLabel>
              <Select
                value={invitationData.tenantRole}
                onChange={(e: SelectChangeEvent) => 
                  setInvitationData(prev => ({ 
                    ...prev, 
                    tenantRole: e.target.value as MemberInvitationData['tenantRole']
                  }))
                }
                label="役割"
              >
                {ROLE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="招待メッセージ"
              multiline
              rows={3}
              value={invitationData.message}
              onChange={(e) => setInvitationData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="新しいメンバーへのメッセージを入力してください..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleSendInvitation} variant="contained" startIcon={<SendIcon />}>
            招待送信
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default MemberManagement;